import EventBase, { AnyEventUpdate, EventUpdate } from './EventBase'
import { MapObject, MapObjectType } from '../../states/WorldState'
import { Point, tupleToPoint } from '../../models/BasicTypes'

export type MapObjectEventType =
  | 'present'
  | 'absent'
  | 'speak'
  | 'move'
  | 'load'

export default interface MapObjectEvent extends EventBase {
  /// MapObject ID
  readonly mapObjectID: string
  /// Type of block event
  readonly type: MapObjectEventType
  /// Created timestamp of this block event
  readonly createdAt: number
}

export interface PresentEvent extends MapObjectEvent {
  /// Type of map object
  readonly mapObjectType: MapObjectType
  /// ID of map object user
  readonly userID: string
  /// Is this present a migration? (from one block to another)
  readonly migration: boolean
  /// Present position
  readonly position: Point
}

export interface AbsentEvent extends MapObjectEvent {
  /// Is this absent a migration? (from one block to another)
  readonly migration: boolean
}

export interface MoveEvent extends MapObjectEvent {
  /// Source of move
  readonly source: Point
  /// Destination of move
  readonly destination: Point
  /// Move ends at
  readonly endsAt: number
}

export interface SpeakEvent extends MapObjectEvent {
  /// Name of player
  readonly name: string
  /// Speak message
  readonly message: string
  /// Local ID of the event
  readonly localID: string
}

/// Event that's only for world component
export interface LoadDataEvent extends MapObjectEvent {
  /// Data to be loaded
  readonly data: MapObject
}

export function parseMapObjectEvent (payload: any): Array<MapObjectEvent> {
  switch (payload.type) {
    case 'motion':
      const move: MoveEvent = {
        id: payload.id,
        type: 'move',
        source: tupleToPoint(payload.source),
        destination: tupleToPoint(payload.destination),
        createdAt: payload.created_at * 1000,
        mapObjectID: payload.map_object.id,
        endsAt: payload.eta * 1000,
        local: false,
        bySelf: false
      }
      return [move]
    case 'speech':
      const speak: SpeakEvent = {
        id: payload.id,
        type: 'speak',
        createdAt: payload.created_at * 1000,
        mapObjectID: payload.map_object.id,
        name: payload.name,
        message: payload.message,
        localID: payload.local_id,
        local: false,
        bySelf: false
      }
      return [speak]
    case 'present':
      const present: PresentEvent = {
        id: payload.id,
        type: 'present',
        position: tupleToPoint(payload.location),
        migration: payload.migration,
        createdAt: payload.created_at * 1000,
        mapObjectID: payload.map_object.id,
        mapObjectType: payload.map_object.type,
        userID: payload.map_object.user.id,
        local: false,
        bySelf: false
      }
      return [present]
    case 'absent':
      const absent: AbsentEvent = {
        id: payload.id,
        type: 'absent',
        migration: payload.migration,
        createdAt: payload.created_at,
        mapObjectID: payload.map_object.id,
        local: false,
        bySelf: false
      }
      return [absent]
    case 'snapshot':
      let result: Array<MapObjectEvent> = []
      for (const subevent of payload.subevents) {
        result = result.concat(parseMapObjectEvent(subevent))
      }
      return result
  }
  return []
}

export function isMapObjectEventUpdate (
  eventUpdate: AnyEventUpdate
): eventUpdate is EventUpdate<MapObjectEvent> {
  return eventUpdate.tag === 'map_object'
}

export function isPresentEvent (event: MapObjectEvent): event is PresentEvent {
  return event.type === 'present'
}

export function isAbsentEvent (event: MapObjectEvent): event is AbsentEvent {
  return event.type === 'absent'
}

export function isMoveEvent (event: MapObjectEvent): event is MoveEvent {
  return event.type === 'move'
}

export function isSpeakEvent (event: MapObjectEvent): event is SpeakEvent {
  return event.type === 'speak'
}

export function isLoadDataEvent (
  event: MapObjectEvent
): event is LoadDataEvent {
  return event.type === 'load'
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/io/events/MapObjectEvent.ts